/*
Collection of lazily-created objects that fetch and store data.

Hierarchy:

  Dat          ->      fetch_obs -> {value, error, task}
  Dat -> Maker ->      fetch_obs -> {value, error, task}
  Dat -> Maker -> page_fetch_obs -> {value, error, task}
  Dat -> Maker -> feed_fetch_obs -> {value, error, task}
*/

import * as a from '@mitranim/js/all.mjs'
import * as n from './net.mjs'
import * as c from './conf.mjs'
import * as u from './util.mjs'
import * as dm from './dat-model.mjs'
import * as ds from './dat-sel.mjs'

export * from './dat-model.mjs'
export * from './dat-cat.mjs'
export * from './dat-misc.mjs'
export * from './dat-form-spec.mjs'
export * from './dat-form-spec-service.mjs'
export * from './dat-jsonld.mjs'
export * from './dat-timetable.mjs'
export * from './dat-sel.mjs'

// Also see `PageDat`.
export class Dat extends u.DeCtxed {
  /*
  Patches the class to redefine all getters. When a getter is used,
  the resulting value is cached and reused for all future "calls".
  This allows easy dedup and caching.
  */
  static {a.memGet(this)}

  constructor(ctx) {
    super(ctx)
    this.timer = new u.Interval(c.FRESHNESS_DEFAULT, evict, this)
  }

  clear() {
    for (const val of a.values(this)) {
      if (a.isInst(val, u.Interval)) continue
      val?.clear?.()
    }
  }

  get categories()               {return new CategoriesFetchObs(this.ctx)}
  get languages()                {return new LanguagesFetchObs(this.ctx)}
  get promotionWares()           {return new PromotionWaresFetchObs(this.ctx)}
  get timezones()                {return new TimezonesFetchObs(this.ctx)}
  get serviceStyles()            {return new u.FunFetchObs(n.fetchEnum, this.ctx, `service_style`)}
  get serviceNatures()           {return new u.FunFetchObs(n.fetchEnum, this.ctx, `service_nature`)}
  get serviceGrowthTypes()       {return new u.FunFetchObs(n.fetchEnum, this.ctx, `service_growth_type`)}
  get serviceRelationTypes()     {return new u.FunFetchObs(n.fetchEnum, this.ctx, `service_relation_type`)}
  get gender()                   {return new u.FunFetchObs(n.fetchEnum, this.ctx, `gender`)}
  get siteFeedLinks()            {return new u.FunFetchObs(n.fetchSiteFeedLinks, this.ctx)}
  get serviceFiltersFormSpec()   {return new u.FunFetchObs(n.fetchServiceFiltersFormSpec, this.ctx)}
  get user()                     {return new UserFetchObs(this.ctx)}
  get balance()                  {return new BalanceFetchObs(this.ctx)}
  get subscriptionPurchases()    {return new u.FunFetchObs(n.fetchSubscriptionPurchases, this.ctx)}
  get promotionWarePurchases()   {return new u.FunFetchObs(n.fetchPromotionWarePurchases, this.ctx)}
  get giftCardPurchases()        {return new u.FunFetchObs(n.fetchGiftCardPurchases, this.ctx)}
  get calRelTimetable()          {return new u.FunFetchObs(n.fetchCalRelTimetable, this.ctx)}
  get currentStripeAccount()     {return new u.FunFetchObs(n.fetchCurrentStripeAccount, this.ctx)}
  get expertiseSlugsForPersons() {return new u.FunFetchObs(n.fetchExpertiseSlugsForPersons, this.ctx)}

  get conCatMain()                     {return new u.FetchObsComp(this.categories,           val => val?.conCatMain())}
  get languageSelectables()            {return new u.FetchObsComp(this.languages,            ds.languagesToSelectables)}
  get timezoneSelectables()            {return new u.FetchObsComp(this.timezones,            ds.timezonesToSelectables)}
  get serviceStyleSelectables()        {return new u.FetchObsComp(this.serviceStyles,        u.enumStringsToSelectables)}
  get serviceNatureSelectables()       {return new u.FetchObsComp(this.serviceNatures,       u.enumStringsToSelectables)}
  get serviceGrowthTypeSelectables()   {return new u.FetchObsComp(this.serviceGrowthTypes,   u.enumStringsToSelectables)}
  get serviceRelationTypeSelectables() {return new u.FetchObsComp(this.serviceRelationTypes, u.enumStringsToSelectables)}
  get genderSelectables()              {return new u.FetchObsComp(this.gender,               u.enumStringsToSelectables)}

  get feedPersons() {return new dm.AggPersons()}

  get articles() {return new u.FunMaker(key => new ArticleFetchObs(this.ctx, key))}
  article(key) {return this.articles.get(key)}

  get articlePages() {return new u.FunMaker(key => new ArticlePageFetchObs(this.ctx, key))}
  articlePage(key) {return this.articlePages.get(key)}
  relArticlePage(key) {return this.articlePage(n.paramsRelArticlePage(key))}

  get practitioners() {return new u.FunMaker(key => new PractFetchObs(this.ctx, key))}
  practitioner(key) {return this.practitioners.get(key)}

  get practitionersBySlug() {return new u.FunMaker(key => new PractBySlugFetchObs(this.ctx, key))}
  practitionerBySlug(key) {return this.practitionersBySlug.get(key)}

  get practPages() {return new u.FunMaker(key => new PractPageFetchObs(this.ctx, key, this.feedPersons))}
  practPage(key) {return this.practPages.get(key)}
  relPractPage(key) {return this.practPage(key)}

  get subscriptions() {return new u.FunMaker(key => new SubscriptionPageFetchObs(this.ctx, key))}
  subscriptionPage(key) {return this.subscriptions.get(key)}

  get orgPages() {return new u.FunMaker(key => new FakeOrgPageFetchObs(this.ctx, key, this.feedPersons))}
  orgPage(key) {return this.orgPages.get(key)}

  get luminaries() {return new u.FunMaker(key => new LuminaryFetchObs(this.ctx, key))}
  luminary(key) {return this.luminaries.get(key)}

  get luminaryPages() {return new u.FunMaker(key => new LuminaryPageFetchObs(this.ctx, key))}
  luminaryPage(key) {return this.luminaryPages.get(key)}

  get latestPublicServices() {return new u.FunMaker(key => new ServiceLatestPublicFetchObs(this.ctx, key))}
  serviceLatestPublic(key) {return this.latestPublicServices.get(key)}

  // Caution: this may return an approved or hidden version!
  // For public display, use `.serviceLatestPublic`.
  get latestServices() {return new u.FunMaker(key => new ServiceLatestFetchObs(this.ctx, key))}
  serviceLatest(key) {return this.latestServices.get(key)}

  get vids() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchVidLatest, this.ctx, key))}
  vid(key) {return this.vids.get(key)}

  get servicePages() {return new u.FunMaker(key => new ServicePageFetchObs(this.ctx, key))}
  servicePage(key) {return this.servicePages.get(key)}
  relServicePage(key) {return this.servicePage(n.paramsRelServicePage(key))}

  get serviceTimetables() {return new u.FunMaker(key => new ServiceTimetableFetchObs(this.ctx, key))}
  serviceTimetable(key) {return this.serviceTimetables.get(key)}

  get vidFeeds() {return new u.FunMaker(key => new u.FunFeedFetchObs(this.ctx, n.vidFeedFetchFirstPage, n.vidFeedFetchNextPage, key))}
  vidFeed(key) {return this.vidFeeds.get(key)}

  get faqFeeds() {return new u.FunMaker(key => new u.FunFeedFetchObs(this.ctx, n.faqFeedFetchFirstPage, n.faqFeedFetchNextPage, key))}
  faqFeed(key) {return this.faqFeeds.get(key)}

  get msgFeeds() {return new u.FunMaker(key => new u.FunFeedFetchObs(this.ctx, n.msgFeedFetchFirstPage, n.msgFeedFetchNextPage, key))}
  msgFeed(key) {return this.msgFeeds.get(key)}

  get reviewsBySubject() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchReviewBySubject, this.ctx, key))}
  reviewBySubject(key) {return this.reviewsBySubject.get(key)}

  get reviewsBySubjectOpt() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchReviewBySubjectOpt, this.ctx, key))}
  reviewBySubjectOpt(key) {return this.reviewsBySubjectOpt.get(key)}

  get reviewFeeds() {return new u.FunMaker(key => new ReviewFeedFetchObs(this.ctx, key))}
  reviewFeed(key) {return this.reviewFeeds.get(key)}

  get calAbsTimespansById() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchCalAbsTimespan, this.ctx, key))}
  calAbsTimespanById(key) {return this.calAbsTimespansById.get(key)}

  get calAbsTimespans() {return new u.FunMaker(key => new CalAbsTimespansFetchObs(this.ctx, key))}
  calAbsTimespan(key) {return this.calAbsTimespans.get(key)}

  get paymentSubjects() {return new u.FunMaker(key => new PaymentSubjectFetchObs(this.ctx, key))}
  paymentSubject(key) {return this.paymentSubjects.get(key)}

  // For "load more" functionality.
  get bookingPages() {return new u.FunMaker(key => new BookingPageFetchObs(this.ctx, key))}
  bookingPage(key) {return this.bookingPages.get(key)}

  get giftCards() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchGiftCard, this.ctx, key))}
  giftCard(key) {return this.giftCards.get(key)}

  get dailyMeetingTokens() {return new u.FunMaker(key => new u.FunFetchObs(n.fetchDailyMeetingToken, this.ctx, key))}
  dailyMeetingToken(key) {return this.dailyMeetingTokens.get(key)}
}

/*
Variant of `Dat` intended for transient caching. This object should be created
and discarded for each page (pushstate transition). Unlike `Dat`, this caches
data in promises rather than `TaskObs`. Using promises rather than task
observables is MUCH simpler, but data can be accessed only asynchronously.

TODO better name.
*/
export class PageDat extends u.Ctxed {
  static {a.memGet(this)}
  get inp() {return new dm.BukInp()}
  get buk() {return new dm.BukOut()}

  async refresh() {
    if (this.inp.isValid()) await this.bukMut(this.inp)
    return this
  }

  async bukMut(inp) {return this.buk.mut(await n.fetchBuk(this.ctx, inp))}

  // Fetches a buket without mutating `.buk`.
  async bukFetch(inp) {return new dm.BukOut(await n.fetchBuk(this.ctx, inp))}

  // Questionable implementation.
  async refetchBalance() {
    this.buk.get.units.balance = (await this.bukFetch({get: {units: {balance: true}}})).get.units.balance
    return this.buk
  }
}

function evict(tar) {
  for (const [key, val] of a.entries(tar)) evictAt(tar, key, val)
}

function evictAt(tar, key, val) {
  if (a.isMap(val)) {
    evict(val)
    return
  }

  if (
    true
    && a.isInst(val, u.TaskObs)
    && val.isForgotten(c.FRESHNESS_DEFAULT)
    && !u.hasSubs(val)
  ) {
    delAt(tar, key)
  }
}

function delAt(tar, key) {
  if (a.isMap(tar)) tar.delete(key)
  else delete a.reqStruct(tar)[key]
}

// TODO switch to `BukPerson`, merge `Person` and `BukPerson` into one class.
export class UserFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AggPerson}
  fetch(...args) {return n.fetchUserOpt(...args)}

  // Known issue: this doesn't update the previous value in the observable.
  // That's because we have a type mismatch between person GET and PATCH
  // endpoints. To fix this, we should update the person using the buket API.
  async update(body) {
    return new this.cls(await n.updateCurrentPerson(...this.args, body))
  }
}

export class PractFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AggPerson}
  fetch(...args) {return n.fetchPractitioner(...args)}
}

export class PractBySlugFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AggPerson}
  fetch(...args) {return n.fetchPractitionerBySlug(...args)}
}

// TODO switch to buket API.
export class PractPageFetchObs extends u.ClsPageObs {
  get cls() {return dm.AggPersonPage}
  fetch(...args) {return n.fetchPractFeedPage(...args)}
}

// TODO switch to buket API.
export class FakeOrgPageFetchObs extends PractPageFetchObs {
  fetch(...args) {return n.fetchFakeOrgFeedPage(...args)}
}

export class ArticleFetchObs extends u.ClsFetchObs {
  get cls() {return dm.ObsAggArticle}
  fetch(...args) {return n.fetchArticle(...args)}
}

export class ArticlePageFetchObs extends u.BukPageFetchObs {
  inp(src) {
    const inp = new dm.BukInp()
    inp.get.pages.articles.add(this.pageInp(src))
    dm.BukArticle.mutInpSets(inp)
    return inp
  }

  page(buk) {return buk.get.pages.articles.getReq(this.key())}
}

export class ServiceLatestPublicFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AggService}
  fetch(...args) {return n.fetchServiceLatestPublic(...args)}
}

export class ServiceLatestFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AggService}
  fetch(...args) {return n.fetchServiceLatest(...args)}
}

export class ServicePageFetchObs extends u.BukPageFetchObs {
  inp(src) {
    const inp = new dm.BukInp()
    inp.get.pages.services.add(this.pageInp(src))
    dm.BukService.mutInpSetsForFeed(inp)
    return inp
  }

  page(buk) {return buk.get.pages.services.getReq(this.key())}
}

export class ServiceTimetableFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AbsTimetable}
  fetch(...args) {return n.fetchServiceTimetable(...args)}
}

export class CalAbsTimespansFetchObs extends u.ClsFetchObs {
  get cls() {return dm.AbsTimetable}
  fetch(...args) {return n.fetchCalAbsTimespans(...args)}
}

export class CategoriesFetchObs extends u.ClsFetchObs {
  get cls() {return dm.Categories}
  fetch(...args) {return n.fetchCategories(...args)}
}

export class TimezonesFetchObs extends u.ClsFetchObs {
  get cls() {return dm.Timezones}
  fetch(...args) {return n.fetchTimezones(...args)}
}

export class PromotionWaresFetchObs extends u.ClsFetchObs {
  get cls() {return dm.PromotionWares}
  fetch(...args) {return n.fetchPromotionWares(...args)}
}

export class LuminaryFetchObs extends u.ClsFetchObs {
  get cls() {return dm.Luminary}
  fetch(...args) {return n.fetchLuminary(...args)}
}

export class LuminaryPageFetchObs extends u.ClsPageObs {
  get cls() {return dm.LuminaryPage}
  fetch(...args) {return n.fetchLuminaryFeedPage(...args)}
}

export class FunFeedFetchObsModel extends u.FunFeedFetchObs {
  get cls() {return dm.ObsModelFeed}
}

export class BookingPageFetchObs extends u.BukPageFetchObs {
  static mutInp(inp, page) {
    if (page) inp.get.pages.bookings.add(page)
    inp.get.sets.bookings.enable()
    inp.get.sets.calAbsTimespans.enable() // For booking-to-service relations.
    inp.get.sets.services.enable()
    inp.get.sets.serviceExts.enable() // For service descriptions.
    inp.get.sets.serviceAggs.enable() // For service images.
    inp.get.sets.persons.enable() // For service sellers.
    inp.get.sets.files.enable() // For service images.
    return inp
  }

  inp(src) {return this.constructor.mutInp(new dm.BukInp(), src)}
  page(buk) {return buk.get.pages.bookings.getReq(this.key())}
}

export class PaymentSubjectFetchObs extends u.ClsFetchObs {
  get cls() {return dm.ExtPaymentSubjectWithMeta}
  fetch(...args) {return n.fetchPaymentSubject(...args)}
}

export class BalanceFetchObs extends u.ClsFetchObs {
  get cls() {return dm.Balance}
  fetch(...args) {return n.fetchBalance(...args)}
}

export class SubscriptionPageFetchObs extends u.ClsPageObs {
  get cls() {return dm.SubscriptionPage}
  fetch(...args) {return n.fetchSubscriptionPage(...args)}
}

// Deprecated, TODO convert our review-related code to buket API.
export class ReviewFeedFetchObs extends u.ClsFeedObs {
  get cls() {return dm.ReviewFeed}
  get first() {return n.reviewFeedFetchFirstPage}
  get next() {return n.reviewFeedFetchNextPage}
}

export class LanguagesFetchObs extends u.ClsFetchObs {
  get cls() {return dm.Languages}
  fetch(...args) {return n.fetchLanguages(...args)}
}
